'use client';

/**
 * VersionGuard — global, mounted once in the root layout. Uses
 * useVersionGuard() to detect bundle drift and raises a persistent toast
 * (see lib/guard-toasts.tsx) offering the user a manual reload. Never reloads
 * automatically. Renders nothing itself.
 *
 * Visibility rules:
 *   - Shown only when the hook reports `stale === true`
 *   - Hidden permanently for this tab when the user clicks "Later" or the ×
 *     (the next poll won't re-show; only a fresh page load can)
 *   - Reload is the toast's action
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useVersionGuard } from '@/hooks/use-version-guard';
import { useStoredValue } from '@/lib/hooks/use-stored-value';
import { GUARD_TOAST_ID, hideGuardToast, showVersionToast } from '@/lib/guard-toasts';
const DISMISS_KEY = 'crucible-version-toast-dismissed-for';
export function VersionGuard() {
  const $ = _c(13);
  const {
    stale,
    serverHash
  } = useVersionGuard();
  const {
    update
  } = useSession();
  let t0;
  if ($[0] !== update) {
    t0 = async function reloadNow() {
      try {
        await update();
      } catch {}
      window.location.reload();
    };
    $[0] = update;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const reloadNow = t0;
  const [dismissedHash, setDismissedHash] = useStoredValue("session", DISMISS_KEY);
  const dismissed = serverHash !== null && dismissedHash === serverHash;
  const visible = stale && !dismissed;
  let t1;
  if ($[2] !== reloadNow) {
    t1 = {
      reloadNow,
      dismiss: _temp
    };
    $[2] = reloadNow;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handlers = useRef(t1);
  let t2;
  if ($[4] !== reloadNow || $[5] !== serverHash || $[6] !== setDismissedHash) {
    t2 = () => {
      handlers.current = {
        reloadNow,
        dismiss: () => {
          if (serverHash) {
            setDismissedHash(serverHash);
          }
        }
      };
    };
    $[4] = reloadNow;
    $[5] = serverHash;
    $[6] = setDismissedHash;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t2);
  let t3;
  let t4;
  if ($[8] !== serverHash || $[9] !== visible) {
    t3 = () => {
      if (!visible) {
        hideGuardToast(GUARD_TOAST_ID.version);
        return;
      }
      showVersionToast({
        serverHash,
        onReload: () => void handlers.current.reloadNow(),
        onLater: () => handlers.current.dismiss()
      });
    };
    t4 = [visible, serverHash];
    $[8] = serverHash;
    $[9] = visible;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(_temp3, t5);
  return null;
}
function _temp3() {
  return _temp2;
}
function _temp2() {
  return hideGuardToast(GUARD_TOAST_ID.version);
}
function _temp() {}